import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function AdminLogin() {
  const { user, login, error, setError, verify2FA } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [provisioningUri, setProvisioningUri] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const result = await login(email, password);
    setBusy(false);
    if (result && result.require2FA) {
      setTotpSecret(result.totpSecret);
      setProvisioningUri(result.provisioningUri);
      setStep(2);
      setError("");
    } else if (result && result.success) {
      navigate("/admin");
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }
    setBusy(true);
    const ok = await verify2FA(email, password, code.trim());
    setBusy(false);
    if (ok) navigate("/admin");
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white border border-sand-300 p-10">
        <div className="eyebrow mb-3">Editorial Access</div>
        
        {step === 1 ? (
          <>
            <h1 className="font-serif text-4xl text-forest-900 mb-8">Sign in</h1>
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="eyebrow block mb-2">Email</label>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-sand-50 border border-sand-300 px-4 py-3 focus:outline-none focus:border-sage"
                  data-testid="login-email" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Password</label>
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-sand-50 border border-sand-300 px-4 py-3 focus:outline-none focus:border-sage"
                  data-testid="login-password" />
              </div>
              {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
              <button disabled={busy} className="btn-primary w-full" data-testid="login-submit">
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="font-serif text-4xl text-forest-900 mb-4">Verification</h1>
            <p className="text-xs text-forest-500 mb-6 leading-relaxed">
              Google Authenticator setup required. Scan the QR code or enter the key manually below, then enter your 6-digit code.
            </p>
            
            <div className="flex flex-col items-center mb-6">
              {provisioningUri && (
                <div className="border border-sand-300 p-2 bg-sand-50 mb-3 rounded-md">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(provisioningUri)}`}
                    alt="Authenticator QR Code"
                    className="w-40 h-40"
                  />
                </div>
              )}
              <div className="text-center w-full bg-sand-50 border border-sand-300 py-2.5 px-3 rounded-md">
                <span className="eyebrow text-[0.6rem] block text-forest-400">Manual Setup Key</span>
                <code className="text-sm font-mono font-bold text-forest-800 tracking-wider select-all">{totpSecret}</code>
              </div>
            </div>

            <form onSubmit={submit2FA} className="space-y-4" data-testid="2fa-form">
              <div>
                <label className="eyebrow block mb-2">6-Digit Code</label>
                <input
                  required
                  type="text"
                  maxLength={6}
                  pattern="\d{6}"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="w-full bg-sand-50 border border-sand-300 px-4 py-3 text-center text-lg tracking-[0.5em] font-mono focus:outline-none focus:border-sage"
                  data-testid="login-2fa-code"
                />
              </div>
              {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
              <button disabled={busy} className="btn-primary w-full" data-testid="2fa-submit">
                {busy ? "Verifying…" : "Verify code"}
              </button>
            </form>
            
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setCode("");
                setError("");
              }}
              className="text-xs text-forest-500 hover:text-forest-900 underline mt-5 block text-center mx-auto"
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
